import * as React from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  clearParameters,
  removeParameter,
  setDateParameters,
  setParameters,
} from "../redux/global/actions";
import {
  getSystemDetails,
  getSystems,
  getSystemsBulk,
} from "../redux/system/actions";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import MultiValueInput from "../inputs/multiValueInput";
import {
  Grid,
  Stack,
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mui/material";
import "./Systems.css";
import SearchDateInput from "../inputs/searchDateInput";
import MultiSelector from "../inputs/MultiSelector";
import {
  downloadCSV,
  downloadJSON,
  extractUserInfoFromToken,
  getLocalStorageSAMLToken,
} from "../utils";
import { DataTable, MaterialSymbol } from "@datalake/towercontrol-banner";
import ExportMenu from "../widgets/ExportMenu";

const SYSTEM_COLUMNS = [
  { id: "system_id", label: "System ID", filter: "text", nowrap: true },
  { id: "creation_date", label: "Creation Date", nowrap: true },
  { id: "modification_date", label: "Modification Date", nowrap: true },
  { id: "name", label: "Name", filter: "select" },
  { id: "sold_to", label: "Sold To", filter: "text" },
  { id: "business_partner", label: "Business Partner", filter: "text" },
  {
    id: "order_id",
    label: "Order ID",
    value: (row) => row?.order?.order_id,
    filter: "text",
  },
];

export default function Systems(props) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryParameters = useSelector((state) => state?.parameters?.systems);

  const [currentSystemId, setCurrentSystemId] = React.useState();
  const [currentBusinessPartner, setCurrentBusinessPartner] = React.useState();
  const [currentSoldTo, setCurrentSoldTo] = React.useState();
  const [currentOrder, setCurrentOrder] = React.useState();
  const [currentEndUser, setCurrentEndUser] = React.useState();
  const [currentSupportPartner, setCurrentSupportPartner] = React.useState();

  const handleExecuteSearch = () => {
    let queryObject = {
      limit: 100,
    };
    if (queryParameters?.systemIds) {
      queryObject.system_id = queryParameters?.systemIds;
    }
    if (queryParameters?.names) {
      queryObject.name = queryParameters?.names;
    }
    if (queryParameters?.businessPartners) {
      queryObject.business_partner = queryParameters?.businessPartners;
    }
    if (queryParameters?.soldTos) {
      queryObject.sold_to = queryParameters?.soldTos;
    }
    if (queryParameters?.order) {
      queryObject.order = queryParameters?.order;
    }
    if (queryParameters?.endUser) {
      queryObject.end_user = queryParameters?.endUser;
    }
    if (queryParameters?.supportPartner) {
      queryObject.support_partner = queryParameters?.supportPartner;
    }
    if (queryParameters?.names) {
      queryObject.name = queryParameters?.names;
    }
    if (
      queryParameters?.creationDateStart &&
      queryParameters?.creationDateEnd
    ) {
      queryObject.creation_date_from = queryParameters?.creationDateStart;
      queryObject.creation_date_to = queryParameters?.creationDateEnd;
    }
    dispatch(getSystems(queryObject));
  };

  const handleExecuteBulkSearch = () => {
    let queryObject = {};
    if (queryParameters?.systemIds) {
      queryObject.system_id = queryParameters?.systemIds;
    }
    if (queryParameters?.names) {
      queryObject.name = queryParameters?.names;
    }
    if (queryParameters?.businessPartners) {
      queryObject.business_partner = queryParameters?.businessPartners;
    }
    if (queryParameters?.soldTos) {
      queryObject.sold_to = queryParameters?.soldTos;
    }
    if (queryParameters?.order) {
      queryObject.order = queryParameters?.order;
    }
    if (queryParameters?.endUser) {
      queryObject.end_user = queryParameters?.endUser;
    }
    if (queryParameters?.supportPartner) {
      queryObject.support_partner = queryParameters?.supportPartner;
    }
    if (queryParameters?.names) {
      queryObject.name = queryParameters?.names;
    }
    if (
      queryParameters?.creationDateStart &&
      queryParameters?.creationDateEnd
    ) {
      queryObject.creation_date_from = queryParameters?.creationDateStart;
      queryObject.creation_date_to = queryParameters?.creationDateEnd;
    }
    const saml_token = getLocalStorageSAMLToken();
    const infoToken = extractUserInfoFromToken(saml_token);
    queryObject.email = infoToken.email;
    queryObject.request_user = infoToken.username;
    dispatch(getSystemsBulk(queryObject));
  };

  const nameValues = ["OXE", "OXO"];

  function handleSelectSystem(systemId) {
    dispatch(getSystemDetails(systemId));
    navigate("/systems/details/" + systemId);
  }

  const handleDeleteSystemId = (chipToDelete) => () => {
    dispatch(removeParameter({ systems: { systemIds: [chipToDelete] } }));
  };

  const handleClearSystemIds = () => {
    dispatch(clearParameters({ systems: "systemIds" }));
  };

  const handleDeleteNames = (chipToDelete) => () => {
    dispatch(removeParameter({ systems: { names: [chipToDelete] } }));
  };

  const handleClearNames = () => {
    dispatch(clearParameters({ systems: "names" }));
  };

  const handleDeleteBusinessPartner = (chipToDelete) => () => {
    dispatch(
      removeParameter({ systems: { businessPartners: [chipToDelete] } }),
    );
  };

  const handleClearBusinessPartners = () => {
    dispatch(clearParameters({ systems: "businessPartners" }));
  };

  const handleDeleteSoldTo = (chipToDelete) => () => {
    dispatch(removeParameter({ systems: { soldTos: [chipToDelete] } }));
  };

  const handleClearSoldTo = () => {
    dispatch(clearParameters({ systems: "soldTos" }));
  };

  const handleDeleteOrder = (chipToDelete) => () => {
    dispatch(removeParameter({ systems: { order: [chipToDelete] } }));
  };

  const handleClearOrder = () => {
    dispatch(clearParameters({ systems: "order" }));
  };

  const handleDeleteEndUser = (chipToDelete) => () => {
    dispatch(removeParameter({ systems: { endUser: [chipToDelete] } }));
  };

  const handleClearEndUser = () => {
    dispatch(clearParameters({ systems: "endUser" }));
  };

  const handleDeleteSupportPartner = (chipToDelete) => () => {
    dispatch(removeParameter({ systems: { supportPartner: [chipToDelete] } }));
  };

  const handleClearSupportPartner = () => {
    dispatch(clearParameters({ systems: "supportPartner" }));
  };
  const handleExport = (systems, format = "csv") => {
    const fields = [
      { name: "system_id" },
      { name: "business_partner" },
      { name: "cpu_id1" },
      { name: "cpu_id2" },
      { name: "creation_date" },
      { name: "creation_note" },
      { name: "description" },
      { name: "inactive_reason" },
      { name: "modification_date" },
      { name: "name" },
      { name: "order_id", value: "order.order_id" },
      { name: "purchase_order", value: "order.purchase_order" },
      { name: "part_number" },
      { name: "product" },
      { name: "release" },
      { name: "sold_to" },
      { name: "status" },
      { name: "support_partner" },
    ];
    if (format === "csv") {
      downloadCSV(fields, "system_export", ",", systems);
    } else {
      downloadJSON(fields, "system_export", systems);
    }
  };
  const systems = useSelector((state) => {
    if (state.filteredSystems) {
      return state.filteredSystems.map((system) => {
        return {
          system_id: system?.system_id,
          support_partner: system?.support_partner?.company_id,
          status: system?.status,
          sold_to: system?.sold_to?.applicant_id,
          release: system?.release,
          product: system?.product,
          part_number: system?.part_number,
          order: system?.order,
          name: system?.name,
          modification_date: system?.modification_date,
          inactive_reason: system?.inactive_reason,
          description: system?.description,
          creation_note: system?.creation_note,
          creation_date: system?.creation_date,
          cpu_id1: system?.cpu_id1,
          cpu_id2: system?.cpu_id2,
          comments: system?.comments,
          business_partner: system?.business_partner?.company_id,
          children: system?.children,
        };
      });
    } else return [];
  });

  const [openBulkConfirmation, setOpenBulkConfirmation] = React.useState(false);
  const [openBulkMessage, setOpenBulkMessage] = React.useState(false);

  const handleKeydown = (event) => {
    if (event.key == "Enter") {
      event.stopPropagation();
      event.preventDefault();
      setTimeout(() => {
        handleExecuteSearch();
      }, 0);
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeydown);
    return () => {
      document.removeEventListener("keydown", handleKeydown);
    };
  }, []);

  return (
    <React.Fragment>
      <Dialog open={openBulkMessage} maxWidth="xs" fullWidth>
        <DialogContent>
          <Typography variant="body2">
            Your query has been sent. You can follow the status in the Bulk
            Report page or wait for the Rainbow message that will announce the
            end of the export.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              setOpenBulkMessage(false);
            }}
          >
            Ok
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openBulkConfirmation} maxWidth="xs" fullWidth>
        <DialogContent>
          <Typography variant="body2">
            This query could take a few minutes, are you sure you want to
            continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              handleExecuteBulkSearch();
              setOpenBulkConfirmation(false);
              setOpenBulkMessage(true);
            }}
          >
            Yes
          </Button>

          <Button variant="text" onClick={() => setOpenBulkConfirmation(false)}>
            No
          </Button>
        </DialogActions>
      </Dialog>
      <MultiValueInput
        setCurrentParam={setCurrentSystemId}
        setQueryParams={setParameters}
        currentParam={currentSystemId}
        queryParams={queryParameters?.systemIds}
        handleDelete={handleDeleteSystemId}
        handleClear={handleClearSystemIds}
        label="System ID"
        family="systems"
        type="systemIds"
      />
      <MultiValueInput
        setCurrentParam={setCurrentBusinessPartner}
        setQueryParams={setParameters}
        currentParam={currentBusinessPartner}
        queryParams={queryParameters?.businessPartners}
        handleDelete={handleDeleteBusinessPartner}
        handleClear={handleClearBusinessPartners}
        label="Business Partner"
        family="systems"
        type="businessPartners"
      />
      <MultiValueInput
        setCurrentParam={setCurrentSoldTo}
        setQueryParams={setParameters}
        currentParam={currentSoldTo}
        queryParams={queryParameters?.soldTos}
        handleDelete={handleDeleteSoldTo}
        handleClear={handleClearSoldTo}
        label="Sold To"
        family="systems"
        type="soldTos"
      />
      <MultiValueInput
        setCurrentParam={setCurrentOrder}
        setQueryParams={setParameters}
        currentParam={currentOrder}
        queryParams={queryParameters?.order}
        handleDelete={handleDeleteOrder}
        handleClear={handleClearOrder}
        label="SAP Order"
        family="systems"
        type="order"
      />
      <MultiValueInput
        setCurrentParam={setCurrentEndUser}
        setQueryParams={setParameters}
        currentParam={currentEndUser}
        queryParams={queryParameters?.endUser}
        handleDelete={handleDeleteEndUser}
        handleClear={handleClearEndUser}
        label="End Customer ID"
        family="systems"
        type="endUser"
      />
      <MultiValueInput
        setCurrentParam={setCurrentSupportPartner}
        setQueryParams={setParameters}
        currentParam={currentSupportPartner}
        queryParams={queryParameters?.supportPartner}
        handleDelete={handleDeleteSupportPartner}
        handleClear={handleClearSupportPartner}
        label="Support Partner"
        family="systems"
        type="supportPartner"
      />
      <MultiSelector
        setQueryParams={setParameters}
        queryParams={queryParameters?.names}
        handleDelete={handleDeleteNames}
        handleClear={handleClearNames}
        label="Name"
        family="systems"
        type="names"
        options={nameValues}
      />
      <Grid>
        <SearchDateInput
          title={"Creation Date"}
          setFilterDate={setDateParameters}
          family="systems"
          type="creationDateStart"
          type2="creationDateEnd"
        />
      </Grid>
      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button
          variant="contained"
          startIcon={<MaterialSymbol name="search" size="button" />}
          onClick={handleExecuteSearch}
        >
          Search
        </Button>
        <Button
          variant="outlined"
          startIcon={<MaterialSymbol name="cloud_download" size="button" />}
          onClick={() => setOpenBulkConfirmation(true)}
        >
          Bulk Export
        </Button>
      </Stack>
      {systems.length > 0 && (
        <DataTable
          title="Systems"
          subtitle={
            systems.length === 100
              ? `${systems.length} results, use bulk export for more`
              : `${systems.length} results`
          }
          columns={SYSTEM_COLUMNS}
          rows={systems}
          getRowId={(row, i) => `${row?.system_id}-${i}`}
          onRowClick={(row) => handleSelectSystem(row?.system_id)}
          defaultRowsPerPage={25}
          size="small"
          actions={(rows) => (
            <ExportMenu
              disabled={!rows.length}
              onExport={(format) => handleExport(rows, format)}
            />
          )}
          sx={{ mt: 3 }}
        />
      )}
    </React.Fragment>
  );
}
